import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  getSpeakerMe, updateSpeakerProfile, uploadSpeakerMaterial, deleteSpeakerMaterial,
  downloadSpeakerMaterial, sendSpeakerContactMessage,
  type SpeakerPortalData, type SpeakerMaterial, type SpeakerEdition,
} from '../api/speaker';

function formatBytes(bytes?: number | null) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fileIcon(type: string) {
  switch (type) {
    case 'pdf': return '📄';
    case 'ppt': case 'pptx': return '📊';
    case 'video': case 'recording': return '🎬';
    case 'link': return '🔗';
    default: return '📎';
  }
}


// ─── Profile Editor ───────────────────────────────────────────────────────────
function ProfileEditor({ token, data, onSaved }: {
  token: string;
  data: SpeakerPortalData;
  onSaved: (updated: SpeakerPortalData) => void;
}) {
  const [bio, setBio] = useState(data.speaker.bio ?? '');
  const [azienda, setAzienda] = useState(data.speaker.azienda ?? '');
  const [linkedin, setLinkedin] = useState(data.speaker.linkedin_url ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const photoRef = useRef<HTMLInputElement>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('bio', bio);
      fd.append('azienda', azienda);
      fd.append('linkedin_url', linkedin);
      if (photoRef.current?.files?.[0]) fd.append('photo', photoRef.current.files[0]);
      const updated = await updateSpeakerProfile(token, fd);
      onSaved({ ...data, speaker: updated });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave}>
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', flexShrink: 0,
          overflow: 'hidden', background: 'var(--gray-100)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {data.speaker.photo_url
            ? <img src={data.speaker.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: '2rem', color: 'var(--gray-300)' }}>👤</span>
          }
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 700, fontSize: '1.25rem', color: 'var(--pub-navy)', fontFamily: 'var(--font-public)' }}>
            {data.speaker.nome} {data.speaker.cognome}
          </div>
          <div className="form-group" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
            <label className="form-label" style={{ fontSize: '0.8125rem' }}>Aggiorna foto (JPG/PNG, max 5MB)</label>
            <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" className="form-control" style={{ fontSize: '0.875rem' }} />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Azienda / Ente</label>
        <input className="form-control" value={azienda} onChange={e => setAzienda(e.target.value)} placeholder="Es. Università di Torino" />
      </div>

      <div className="form-group">
        <label className="form-label">Biografia</label>
        <textarea className="form-control" rows={4} value={bio}
          onChange={e => setBio(e.target.value)}
          placeholder="Breve descrizione del tuo profilo professionale…"
          style={{ resize: 'vertical' }} />
      </div>

      <div className="form-group">
        <label className="form-label">LinkedIn URL</label>
        <input className="form-control" type="url" value={linkedin}
          onChange={e => setLinkedin(e.target.value)}
          placeholder="https://linkedin.com/in/..." />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Salvataggio…' : 'Salva profilo'}
        </button>
        {saved && <span style={{ color: 'var(--success)', fontSize: '0.875rem', fontWeight: 500 }}>✓ Salvato</span>}
      </div>
    </form>
  );
}

// ─── Upload Material Modal ────────────────────────────────────────────────────
function UploadMaterialModal({ token, editions, onClose, onUploaded }: {
  token: string;
  editions: SpeakerEdition[];
  onClose: () => void;
  onUploaded: (m: SpeakerMaterial) => void;
}) {
  const [editionId, setEditionId] = useState(editions[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('pdf');
  const [externalUrl, setExternalUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const isFileType = ['pdf', 'ppt', 'pptx', 'video'].includes(type);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !editionId) return;
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('edition_id', editionId);
      fd.append('title', title);
      fd.append('description', description);
      fd.append('type', type);
      fd.append('available_from', 'registration');
      if (externalUrl) fd.append('external_url', externalUrl);
      if (isFileType && fileRef.current?.files?.[0]) fd.append('file', fileRef.current.files[0]);
      const material = await uploadSpeakerMaterial(token, fd);
      onUploaded(material);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Errore durante il caricamento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
    }} onClick={onClose}>
      <div className="card" style={{ width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0 }}>Carica materiale</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
        </div>

        <form onSubmit={handleUpload}>
          {editions.length > 1 && (
            <div className="form-group">
              <label className="form-label">Corso</label>
              <select className="form-control" value={editionId} onChange={e => setEditionId(e.target.value)}>
                {editions.map(ed => (
                  <option key={ed.id} value={ed.id}>{ed.course_name} — {ed.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select className="form-control" value={type} onChange={e => setType(e.target.value)}>
              <option value="pdf">PDF</option>
              <option value="pptx">PowerPoint (PPTX)</option>
              <option value="ppt">PowerPoint (PPT)</option>
              <option value="video">Video (MP4/WebM)</option>
              <option value="link">Link esterno</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Titolo *</label>
            <input className="form-control" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Es. Slide della presentazione" required />
          </div>

          <div className="form-group">
            <label className="form-label">Descrizione</label>
            <textarea className="form-control" rows={2} value={description}
              onChange={e => setDescription(e.target.value)} style={{ resize: 'vertical' }} />
          </div>

          {isFileType && (
            <div className="form-group">
              <label className="form-label">File (max 100MB)</label>
              <input ref={fileRef} type="file" className="form-control"
                accept=".pdf,.ppt,.pptx,.mp4,.webm" />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{type === 'link' ? 'URL *' : 'URL esterno (opzionale)'}</label>
            <input className="form-control" type="url" value={externalUrl}
              onChange={e => setExternalUrl(e.target.value)} placeholder="https://…" />
          </div>

          {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
            disabled={!title || !editionId || saving}>
            {saving ? 'Caricamento…' : 'Carica'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Materials Tab ────────────────────────────────────────────────────────────
function TabMateriali({ token, materials, onDelete }: {
  token: string;
  materials: SpeakerMaterial[];
  onDelete: (id: string, title: string) => void;
}) {
  // Group by course_name
  const groups = materials.reduce<Record<string, SpeakerMaterial[]>>((acc, m) => {
    (acc[m.course_name] ??= []).push(m);
    return acc;
  }, {});
  const courseNames = Object.keys(groups);

  if (materials.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '4rem 2rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📂</div>
        <div style={{ fontFamily: 'var(--font-public)' }}>Nessun materiale caricato.</div>
        <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Carica slide, PDF o video dei tuoi corsi.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {courseNames.map(courseName => (
        <div key={courseName}>
          <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.875rem', margin: '0 0 0.875rem' }}>
            {courseName}
          </p>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {groups[courseName].map(m => (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem',
                background: '#fff', borderRadius: 10, border: '1px solid var(--gray-100)',
              }}>
                <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{fileIcon(m.type)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</div>
                  {m.description && <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{m.description}</div>}
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.125rem' }}>
                    {m.type.toUpperCase()}{m.file_size_bytes ? ` · ${formatBytes(m.file_size_bytes)}` : ''}
                    {' · '}{new Date(m.created_at).toLocaleDateString('it-IT')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  {m.type !== 'link' && (
                    <button className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8125rem' }}
                      onClick={() => downloadSpeakerMaterial(token, m.id, m.title)}>
                      Scarica
                    </button>
                  )}
                  {m.type === 'link' && m.external_url && (
                    <a href={m.external_url} target="_blank" rel="noopener noreferrer"
                      className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8125rem' }}>
                      Apri
                    </a>
                  )}
                  <button className="btn btn-danger" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8125rem' }}
                    onClick={() => onDelete(m.id, m.title)}>
                    Elimina
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SpeakerPortalPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SpeakerPortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [activeTab, setActiveTab] = useState<'profilo' | 'materiali' | 'supporto'>('profilo');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactResult, setContactResult] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    getSpeakerMe(token)
      .then(setData)
      .catch(() => setError('Link non valido o scaduto.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDeleteMaterial = async (id: string, title: string) => {
    if (!confirm(`Eliminare "${title}"?`)) return;
    await deleteSpeakerMaterial(token!, id);
    setData(prev => prev ? { ...prev, materials: prev.materials.filter(m => m.id !== id) } : prev);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F0F4F8', display: 'flex', flexDirection: 'column' }}>
        <div className="loading-container" style={{ flex: 1 }}><div className="spinner spinner-lg" /></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#F0F4F8', display: 'flex', flexDirection: 'column' }}>
        <main className="public-main" style={{ flex: 1 }}>
          <div className="alert alert-danger">{error || 'Link non valido. Contatta l\'organizzazione per riceverne uno nuovo.'}</div>
        </main>
      </div>
    );
  }

  const { speaker, editions, materials } = data;

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactSending(true);
    setContactResult(null);
    try {
      await sendSpeakerContactMessage(token!, contactSubject, contactMessage);
      setContactResult({ ok: true, msg: 'Messaggio inviato. Ti risponderemo al più presto.' });
      setContactSubject('');
      setContactMessage('');
    } catch {
      setContactResult({ ok: false, msg: 'Errore durante l\'invio. Riprova o scrivi a marketing@switchonmedia.it.' });
    } finally {
      setContactSending(false);
    }
  };

  const tabs: { id: 'profilo' | 'materiali' | 'supporto'; label: string; icon: string }[] = [
    { id: 'profilo', label: 'Il mio profilo', icon: '👤' },
    { id: 'materiali', label: `Materiali (${materials.length})`, icon: '📁' },
    { id: 'supporto', label: 'Supporto', icon: '💬' },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>



      {/* Body: two columns */}
      <div style={{ display: 'flex', flex: 1 }}>

        {/* ── Left sidebar ── */}
        <div style={{
          width: 620, flexShrink: 0, background: '#fff',
          borderRight: '1px solid var(--gray-100)',
          display: 'flex', flexDirection: 'column',
          padding: '2.5rem 2rem',
          overflowY: 'auto',
        }}>
          {/* Speaker photo + name */}
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--pub-orange)', marginBottom: '0.75rem', fontFamily: 'var(--font-public)' }}>
              Portale Relatori
            </p>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
                overflow: 'hidden', background: 'var(--gray-100)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {speaker.photo_url
                  ? <img src={speaker.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: '1.75rem', color: 'var(--gray-300)' }}>👤</span>
                }
              </div>
              <div>
                <h2 style={{ color: 'var(--pub-navy)', fontFamily: 'var(--font-public)', fontWeight: 800, fontSize: '1.25rem', margin: 0, lineHeight: 1.2 }}>
                  {speaker.nome} {speaker.cognome}
                </h2>
                {speaker.azienda && (
                  <p style={{ color: 'var(--gray-500)', margin: '0.2rem 0 0', fontSize: '0.875rem' }}>{speaker.azienda}</p>
                )}
              </div>
            </div>
          </div>

          {/* Editions list */}
          <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-400)', margin: 0 }}>
              I miei corsi
            </p>
            {editions.map(ed => (
              <div key={ed.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: '0.1rem' }}>📚</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--gray-700)' }}>{ed.course_name}</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>{ed.label}</div>
                  {ed.scheduled_at && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.1rem' }}>
                      {new Date(ed.scheduled_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 'auto', paddingTop: '2rem', borderTop: '1px solid var(--gray-100)' }}>
            <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)', margin: 0 }}>
              Problemi? <a href="mailto:marketing@switchonmedia.it" style={{ color: 'var(--pub-blue)' }}>marketing@switchonmedia.it</a>
            </p>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div style={{ flex: 1, background: '#F0F4F8', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

          {/* Tab bar */}
          <div style={{ background: '#fff', borderBottom: '1px solid var(--gray-100)', padding: '0 2rem', display: 'flex', gap: 0, flexShrink: 0 }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '1rem 1.25rem',
                  border: 'none', borderBottom: activeTab === tab.id ? '2px solid var(--pub-navy)' : '2px solid transparent',
                  background: 'transparent', cursor: 'pointer',
                  fontWeight: activeTab === tab.id ? 700 : 400,
                  color: activeTab === tab.id ? 'var(--pub-navy)' : 'var(--gray-500)',
                  fontFamily: 'var(--font-public)', fontSize: '0.9375rem',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, padding: '2rem 2.5rem' }}>

            {activeTab === 'profilo' && (
              <ProfileEditor token={token!} data={data} onSaved={setData} />
            )}

            {activeTab === 'supporto' && (
              <div style={{ maxWidth: 520 }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1.5rem' }}>
                  Hai domande o problemi tecnici? Compila il form e ti risponderemo al più presto.
                </p>
                {contactResult && (
                  <div className={`alert ${contactResult.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '1.25rem' }}>
                    {contactResult.msg}
                  </div>
                )}
                <form onSubmit={handleContactSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Oggetto *</label>
                    <input
                      className="form-control"
                      value={contactSubject}
                      onChange={e => setContactSubject(e.target.value)}
                      placeholder="Es. Problema con il caricamento dei materiali"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Messaggio *</label>
                    <textarea
                      className="form-control"
                      rows={5}
                      value={contactMessage}
                      onChange={e => setContactMessage(e.target.value)}
                      placeholder="Descrivi il problema o la domanda..."
                      style={{ resize: 'vertical' }}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={contactSending}>
                    {contactSending ? 'Invio in corso…' : 'Invia messaggio'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'materiali' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                  <p style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                    Materiali caricati
                  </p>
                  <button className="btn btn-primary" style={{ fontSize: '0.875rem' }}
                    onClick={() => setShowUpload(true)}>
                    + Carica materiale
                  </button>
                </div>
                <TabMateriali token={token!} materials={materials} onDelete={handleDeleteMaterial} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        background: 'var(--pub-navy)', color: 'rgba(255,255,255,0.55)',
        fontSize: '0.8125rem', textAlign: 'center', padding: '1rem 2rem',
        fontFamily: 'var(--font-public)', flexShrink: 0,
      }}>
        {import.meta.env.VITE_FOOTER_TEXT}
      </footer>

      {showUpload && (
        <UploadMaterialModal
          token={token!}
          editions={editions}
          onClose={() => setShowUpload(false)}
          onUploaded={m => setData(prev => prev ? { ...prev, materials: [...prev.materials, m] } : prev)}
        />
      )}
    </div>
  );
}
